/* eslint-disable no-nested-ternary */
import LoginDispatcher from "@Components/Login/actions";
import { AppState } from "@Store/index";
import { MOBILE_APP_URL_PROPERTY_PAGE_OFFER_SECTION } from "constants/configConstants";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import styles from "./OffersSectionStyles.module.scss";
import { Offer } from "./types";
import {
  getOfferTypeLabel,
  getOfferDisplayLabel,
  getExpiryText,
  calculateSavingsSummary,
  getSortedOffers,
} from "./util";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import getAppLink from "@Utils/getAppLink";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
import OffersStickerIcon from "@Icons/OffersStickerIcon";
import OfferChevronDown from "@Icons/OfferChevronDown";
import OfferItem from "./OfferItem";
import useIsApp from "hooks/useIsApp";
import getCurrencySymbol from "helpers/getCurrencySymbol";
import useWhitelabel from "hooks/useWhitelabel";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import filterOffersByWhitelabel from "./filterOffersByWhitelabel";
import Skeleton from "@Components/Skeleton";

export const OffersSection: FC<any> = ({
  isDesktop = false,
  isAgentView = false,
  isLoading = false,
}) => {
  const { isUserLoggedIn, unsortedOffers, pricing, userLanguage } = useSelector(
    ({ appData, propertyPageData }: AppState) => ({
      isUserLoggedIn: appData.isUserLoggedIn,
      unsortedOffers: propertyPageData.data.active_offers,
      pricing: propertyPageData.data.pricing,
      userLanguage: appData.userLanguage,
    }),
    shallowEqual,
  );
  const offers = getSortedOffers(unsortedOffers);
  const dispatch = useDispatch();
  const customWindowOpen = useCustomWindowOpen();
  const isApp = useIsApp();
  const { isWhitelabel, whitelabelColors, partner_uuid, partnerName } = useWhitelabel();
  const viewMoreOffersColor = isWhitelabel ? whitelabelColors?.cta_color : undefined;

  const filteredOffers = filterOffersByWhitelabel(offers, isWhitelabel, partner_uuid, partnerName);
  // const forInternalPurpose = isInternalUser(userData);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const openOfferParam = query.get("open-offer");
  const openOffer = openOfferParam ? parseInt(openOfferParam, 10) : null;

  const [isExpanded, setIsExpanded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslationHook();

  const viewMoreText = isDesktop
    ? t("propertypage.components.offerssection.viewmoreoffers")
    : "View All";
  const viewLessText = isDesktop
    ? t("propertypage.components.offerssection.viewlessoffers")
    : "View Less";

  const handleClose = () => {
    if (openOfferParam) {
      query.delete("open-offer");
      let newURL = `${window.location.origin}${window.location.pathname}`;
      const qString = query?.toString();
      if (qString) {
        newURL = `${newURL}?${qString}`;
      }
      window.history.replaceState({ ...window.history.state, path: newURL }, "", newURL);
    }
  };

  const handleShouldOpen = (id: number, type: string) => {
    if (type?.trim()?.toLowerCase() === "install_app" && !isDesktop && !isApp) {
      const appLink = getAppLink(MOBILE_APP_URL_PROPERTY_PAGE_OFFER_SECTION);
      customWindowOpen({ url: appLink, target: "_blank", isExternal: true });
      return false;
    }
    // If type is login, user should only able to open modal after login
    // eslint-disable-next-line no-else-return
    else if (type?.trim()?.toLowerCase() === "login" && !isUserLoggedIn) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set("open-offer", id?.toString());
      const newURL = `${window.location.pathname}?${urlParams?.toString()}`;
      window.history.replaceState({ ...window.history.state, path: newURL }, "", newURL);
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({});
      return false;
    }
    return true;
  };

  // Clean up query param after offer modal is triggered (one-time use)
  useEffect(() => {
    if (!isUserLoggedIn && openOfferParam) {
      handleClose();
    }
  }, []);

  const handleToggle = () => {
    if (isExpanded) {
      setIsExpanded(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (sectionRef.current) {
            sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      });
    } else {
      setIsExpanded(true);
    }
  };

  const INITIAL_COUNT = 3;
  const visibleOffers = isExpanded ? filteredOffers : filteredOffers?.slice(0, INITIAL_COUNT);
  const showToggle = filteredOffers?.length > INITIAL_COUNT;

  // Calculate combined savings summary
  const savingsSummary = useMemo(
    () => calculateSavingsSummary(filteredOffers || []),
    [filteredOffers],
  );
  const THRESHOLD_OFFER_VALUE = 90;
  const hasSavings =
    savingsSummary.cashbackTotal >= THRESHOLD_OFFER_VALUE || savingsSummary.extraBenefitsTotal > 0;
  const hasBothSavings =
    savingsSummary.cashbackTotal >= THRESHOLD_OFFER_VALUE && savingsSummary.extraBenefitsTotal > 0;
  const currency = getCurrencySymbol(pricing?.currency);
  const showOffersSkeleton = isLoading;

  const renderOffersSkeleton = () => (
    <div ref={sectionRef} className={styles.container} id="offers">
      <div className={formatClassNames(styles.header, styles.singleOfferHeader)}>
        <h2 className={styles.title}>{t("propertypage.components.offerssection.offerstitle")}</h2>
      </div>

      <div className={styles.divider} />

      <div className={styles.list}>
        <div className={styles.savingsBanner}>
          <Skeleton width="92%" height="14px" addBorderRadius />
        </div>

        {[0, 1, 2].map((index) => (
          <div key={`offer-skeleton-${index}`}>
            <div className={styles.offerItem}>
              <Skeleton width="18px" height="18px" addBorderRadius />
              <div className={styles.offerContent}>
                <Skeleton
                  width={index === 0 ? "65%" : index === 1 ? "78%" : "58%"}
                  height="14px"
                  addBorderRadius
                />
              </div>
              <Skeleton width="12px" height="12px" addBorderRadius />
            </div>
            {index < 2 && <div className={styles.offerSeparator} />}
          </div>
        ))}
      </div>

      <div className={`${styles.viewAllBtn} ${styles.viewAllOffersBtn}`}>
        <Skeleton width="56px" height="14px" addBorderRadius />
      </div>
    </div>
  );

  return (
    <>
      {showOffersSkeleton ? (
        renderOffersSkeleton()
      ) : filteredOffers?.length >= 1 ? (
        <div ref={sectionRef} className={styles.container} id="offers">
          <div className={formatClassNames(styles.header, hasSavings && styles.singleOfferHeader)}>
            <h2 className={styles.title}>
              {t("propertypage.components.offerssection.offerstitle")}
            </h2>
            <div
              className={formatClassNames(
                styles.singleOfferDiv,
                userLanguage == "de" && styles.germanLang,
              )}
            >
              {hasSavings && !hasBothSavings && (
                <div
                  className={formatClassNames(
                    styles.savingsBanner,
                    !hasBothSavings && styles.halfWidth,
                  )}
                >
                  <span className={styles.savingsText}>
                    {savingsSummary.cashbackTotal >= THRESHOLD_OFFER_VALUE ? (
                      <>
                        {t("propertypage.components.offerssection.saveupto")}{" "}
                        <span className={styles.savingsTextBold}>
                          {currency}
                          {savingsSummary.cashbackTotal.toLocaleString()}
                        </span>
                      </>
                    ) : (
                      <>
                        {t("propertypage.components.offerssection.unlockupto")}{" "}
                        <span className={styles.savingsTextBold}>
                          {currency}
                          {savingsSummary.extraBenefitsTotal.toLocaleString()}{" "}
                        </span>
                        {t("propertypage.components.offerssection.inextrabenefits")}
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>
            <div className={styles.sticker}>
              <OffersStickerIcon />
            </div>
          </div>

          <div className={styles.divider} />

          <div className={`${styles.list} ${!showToggle ? styles.listWithBorderRadius : ""}`}>
            {/* Savings Summary Banner */}
            {hasBothSavings && (
              <div
                className={formatClassNames(
                  styles.savingsBanner,
                  !hasBothSavings && styles.halfWidth,
                )}
              >
                <span className={styles.savingsText}>
                  {hasBothSavings ? (
                    <>
                      {t("propertypage.components.offerssection.saveupto")}{" "}
                      <span className={styles.savingsTextBold}>
                        {currency}
                        {savingsSummary.cashbackTotal.toLocaleString()}
                      </span>{" "}
                      + {t("propertypage.components.offerssection.unlockupto")}{" "}
                      <span className={styles.savingsTextBold}>
                        {currency}
                        {savingsSummary.extraBenefitsTotal.toLocaleString()}
                      </span>{" "}
                      {t("propertypage.components.offerssection.inextrabenefits")}
                    </>
                  ) : null}
                </span>
              </div>
            )}

            {visibleOffers?.map((offer: Offer, index: number) => {
              const { name, description, action_type, value, name_zh, type, end_date, tnc, code } =
                offer;
              const displayName = userLanguage !== "en" && name_zh ? name_zh : name;
              // Only show mapped type for agents
              const mappedType = isAgentView && type ? getOfferTypeLabel(type) : "";
              const titleWithType =
                isAgentView && mappedType ? `${displayName} (${mappedType})` : displayName;

              // Get label prefix for offer (Loyalty Bonus, App Only Offer, Bonus Deal, etc.)
              const labelPrefix = t(getOfferDisplayLabel(offer));

              // Get expiry info
              const expiryInfo = getExpiryText(end_date);

              const content =
                action_type?.trim()?.toLowerCase() === "install_app"
                  ? ""
                  : `<strong>Offer</strong> ${description}`;

              const isLastOffer = index === filteredOffers.length - 1;
              const shouldShowSeparator = !isLastOffer;
              const isFirstOffer = index === 0;
              const isHidden = !isExpanded && index >= INITIAL_COUNT;

              return (
                <div
                  key={name}
                  className={`${isFirstOffer ? styles.firstOfferItem : ""} ${
                    isHidden ? styles.hiddenItem : ""
                  }`}
                >
                  <OfferItem
                    title={titleWithType}
                    content={content}
                    onClose={handleClose}
                    shouldOpen={() => handleShouldOpen(value, action_type)}
                    gtagExtraParams={{ offers_id: value, offers_title: name }}
                    defaultOpen={isUserLoggedIn && openOffer !== null && openOffer === value}
                    gtagEventAction="offer"
                    isDesktop={isDesktop}
                    labelPrefix={labelPrefix}
                    expiryInfo={expiryInfo}
                    tnc={tnc}
                    code={code}
                    isInternalUser={isAgentView}
                    action_type={action_type}
                  />
                  {shouldShowSeparator && <div className={styles.offerSeparator} />}
                </div>
              );
            })}
          </div>

          {showToggle && (
            <button
              className={`${styles.viewAllBtn} ${styles.viewAllOffersBtn}`}
              onClick={handleToggle}
              type="button"
              style={viewMoreOffersColor ? { color: viewMoreOffersColor } : undefined}
            >
              <span>{isExpanded ? viewLessText : viewMoreText}</span>
              <div
                className={styles.arrowIcon}
                style={{
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  display: "flex",
                  alignItems: "center",
                  ...(viewMoreOffersColor ? { color: viewMoreOffersColor } : {}),
                }}
              >
                <OfferChevronDown
                  width={12}
                  height={12}
                  {...(viewMoreOffersColor ? { color: viewMoreOffersColor } : {})}
                />
              </div>
            </button>
          )}
        </div>
      ) : null}
    </>
  );
};

OffersSection.displayName = "OffersSection";

export default wrapperHOC(OffersSection, {
  componentName: "OffersSection-PropertyPage",
  showForChina: true,
  showForWhiteLable: true,
});
